import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CommentIcon from '@mui/icons-material/Comment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CompactCardDeck from './CompactCardDeck';
import ItemComments from './ItemComments';
import { getAISuggestion } from '../../utils/api';

const AsyncItemCard = ({
  item,
  votedCount,
  totalVoters,
  currentParticipantVote,
  deckType,
  onVote,
  onReveal,
  onSetFinalEstimate,
  onStartNewRound,
  onPushToJira,
  isFacilitator,
  isDeadlinePassed,
  comments = [],
  votes = [],
  jiraKey = null,
  jiraUrl = null,
  jiraConfigured = false,
  participants = []
}) => {
  const [expanded, setExpanded] = useState(false);
  const [selectedValue, setSelectedValue] = useState(currentParticipantVote);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [isPushing, setIsPushing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // AI Suggestion state
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [showAiSuggestion, setShowAiSuggestion] = useState(false);

  useEffect(() => {
    setSelectedValue(currentParticipantVote);
  }, [currentParticipantVote]);

  // Auto-expand when revealed so facilitator can see controls
  useEffect(() => {
    if ((item.status === 'revealed') && isFacilitator) {
      setExpanded(true);
    }
  }, [item.status, isFacilitator]);

  const isComplete = votedCount >= totalVoters && totalVoters > 0;
  const isRevealed = item.status === 'revealed';
  const isEstimated = item.status === 'estimated';
  const canVote = !isEstimated && !isDeadlinePassed;
  const progressPercentage = totalVoters > 0 ? (votedCount / totalVoters) * 100 : 0;

  const handleVote = (value) => {
    setSelectedValue(value);
    if (onVote) {
      onVote(item.id, value);
    }
  };

  const handleSetFinalEstimate = () => {
    if (selectedEstimate && onSetFinalEstimate) {
      onSetFinalEstimate(item.id, selectedEstimate);
    }
  };

  const handlePushToJira = async () => {
    if (!jiraKey || !selectedEstimate) return;

    setIsPushing(true);
    try {
      if (onPushToJira) {
        await onPushToJira(item.id, jiraKey, selectedEstimate);
        setSnackbar({
          open: true,
          message: `Estimate ${selectedEstimate} pushed to Jira ${jiraKey}`,
          severity: 'success'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to push to Jira',
        severity: 'error'
      });
    } finally {
      setIsPushing(false);
    }
  };

  const handleGetAISuggestion = async () => {
    setIsLoadingAI(true);
    setShowAiSuggestion(true);
    try {
      const suggestion = await getAISuggestion(item.id, {
        title: item.title,
        description: item.description || ''
      });
      setAiSuggestion(suggestion);
      // Auto-select the suggested estimate if available
      if (suggestion.suggestedEstimate) {
        setSelectedEstimate(suggestion.suggestedEstimate);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.msg || 'Failed to get AI suggestion',
        severity: 'error'
      });
      setShowAiSuggestion(false);
    } finally {
      setIsLoadingAI(false);
    }
  };

  const handleUseAISuggestion = () => {
    if (aiSuggestion?.suggestedEstimate) {
      setSelectedEstimate(aiSuggestion.suggestedEstimate);
    }
  };

  const handleDismissAISuggestion = () => {
    setShowAiSuggestion(false);
    setAiSuggestion(null);
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case 'high':
        return 'success';
      case 'medium':
        return 'warning';
      case 'low':
      default:
        return 'default';
    }
  };

  // Calculate vote statistics
  const getVoteStats = () => {
    if (!votes || votes.length === 0) return null;

    const voteValues = votes.map(v => v.value).filter(v => v !== '?' && v !== '☕');
    const numericVotes = voteValues.map(v => parseFloat(v)).filter(v => !isNaN(v));

    if (numericVotes.length === 0) return null;

    const average = numericVotes.reduce((a, b) => a + b, 0) / numericVotes.length;
    const min = Math.min(...numericVotes);
    const max = Math.max(...numericVotes);

    // Count votes by value
    const voteCounts = {};
    voteValues.forEach(v => {
      voteCounts[v] = (voteCounts[v] || 0) + 1;
    });

    // Find most common vote (mode)
    const mode = Object.entries(voteCounts).sort((a, b) => b[1] - a[1])[0]?.[0];

    return { average: average.toFixed(1), min, max, mode, voteCounts };
  };

  const getStatusChip = () => {
    if (isEstimated) {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label={`Estimated: ${item.finalEstimate}`}
          color="success"
          size="small"
        />
      );
    }
    if (isRevealed) {
      return (
        <Chip
          icon={<VisibilityIcon />}
          label="Revealed"
          color="info"
          size="small"
        />
      );
    }
    if (isComplete) {
      return (
        <Chip
          icon={<HowToVoteIcon />}
          label="Ready to Reveal"
          color="primary"
          size="small"
        />
      );
    }
    return (
      <Chip
        label={`${votedCount} / ${totalVoters} voted`}
        variant="outlined"
        size="small"
      />
    );
  };

  const voteStats = getVoteStats();

  return (
    <Card
      elevation={expanded ? 3 : 1}
      sx={{
        mb: 2,
        transition: 'all 0.2s ease-in-out',
        borderLeft: '4px solid',
        borderColor: isEstimated
          ? 'success.main'
          : isRevealed
          ? 'info.main'
          : isComplete
          ? 'primary.main'
          : 'grey.300'
      }}
    >
      <CardContent sx={{ pb: expanded ? 1 : 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box
            sx={{
              flex: 1,
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 }
            }}
            onClick={() => setExpanded(!expanded)}
          >
            <Typography variant="subtitle1" fontWeight="medium" noWrap>
              {item.title}
            </Typography>
            {item.externalId && (
              <Typography variant="caption" color="text.secondary">
                {item.externalId}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 1 }}>
            {getStatusChip()}

            <Badge badgeContent={comments.length} color="secondary" max={99}>
              <CommentIcon fontSize="small" color="action" />
            </Badge>

            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Progress Bar */}
        {!isEstimated && !isRevealed && (
          <Box sx={{ mb: 1 }}>
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: '#2a2a2a',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  bgcolor: isComplete ? 'success.main' : 'primary.main'
                }
              }}
            />
          </Box>
        )}

        {/* Current Vote Indicator */}
        {selectedValue && !isRevealed && !isEstimated && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Your vote:
            </Typography>
            <Chip
              label={selectedValue}
              color="primary"
              size="small"
              variant="filled"
            />
          </Box>
        )}

        {/* Revealed Votes */}
        {(isRevealed || isEstimated) && votes.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Votes:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {votes.map((vote, index) => (
                <Chip
                  key={index}
                  label={`${vote.participant?.name?.split(' ')[0] || 'Unknown'}: ${vote.value}`}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
      </CardContent>

      {/* Expanded Content */}
      <Collapse in={expanded}>
        <CardContent sx={{ pt: 0 }}>
          {/* Description */}
          {item.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, whiteSpace: 'pre-wrap' }}
            >
              {item.description}
            </Typography>
          )}

          {/* Voting Section */}
          {canVote && !isRevealed && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Cast Your Vote
              </Typography>
              <CompactCardDeck
                deckType={deckType}
                selectedValue={selectedValue}
                onSelect={handleVote}
                disabled={isDeadlinePassed}
              />
            </Box>
          )}

          {/* Facilitator Actions - Before Reveal */}
          {isFacilitator && isComplete && !isRevealed && !isEstimated && (
            <Box sx={{ mb: 2 }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => onReveal && onReveal(item.id)}
                startIcon={<VisibilityIcon />}
              >
                Reveal Votes
              </Button>
            </Box>
          )}

          {/* Facilitator Actions - After Reveal */}
          {isFacilitator && isRevealed && !isEstimated && (
            <Box sx={{ mb: 2 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Set Final Estimate
              </Typography>

              {/* Vote Statistics */}
              {voteStats && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#141414', borderRadius: 1, border: '1px solid #2a2a2a' }}>
                  <Typography variant="body2" color="text.secondary">
                    Average: <strong>{voteStats.average}</strong> |
                    Range: <strong>{voteStats.min} - {voteStats.max}</strong> |
                    Most common: <strong>{voteStats.mode}</strong>
                  </Typography>
                </Box>
              )}

              {/* AI Suggestion Section */}
              {!showAiSuggestion && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleGetAISuggestion}
                    disabled={isLoadingAI}
                    startIcon={isLoadingAI ? <CircularProgress size={16} /> : <SmartToyIcon />}
                    size="small"
                  >
                    Get AI Suggestion
                  </Button>
                </Box>
              )}

              {showAiSuggestion && (
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    bgcolor: 'rgba(59, 130, 246, 0.1)',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'rgba(59, 130, 246, 0.3)'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <SmartToyIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle2" color="primary.main">
                      AI Suggestion
                    </Typography>
                  </Box>

                  {isLoadingAI ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">
                        Analyzing similar items...
                      </Typography>
                    </Box>
                  ) : aiSuggestion ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Typography variant="body1">
                          Suggested: <strong>{aiSuggestion.suggestedEstimate || 'N/A'}</strong>
                        </Typography>
                        <Chip
                          label={`${aiSuggestion.confidence} confidence`}
                          size="small"
                          color={getConfidenceColor(aiSuggestion.confidence)}
                        />
                      </Box>

                      {aiSuggestion.similarItems && aiSuggestion.similarItems.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Based on similar items:
                          </Typography>
                          {aiSuggestion.similarItems.slice(0, 3).map((similar, idx) => (
                            <Tooltip
                              key={idx}
                              title={similar.title}
                              placement="top"
                            >
                              <Chip
                                label={`${similar.title.substring(0, 20)}${similar.title.length > 20 ? '...' : ''} (${similar.estimate})`}
                                size="small"
                                variant="outlined"
                                sx={{ mr: 0.5, mb: 0.5 }}
                              />
                            </Tooltip>
                          ))}
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                        {aiSuggestion.suggestedEstimate && (
                          <Button
                            variant="contained"
                            size="small"
                            color="primary"
                            onClick={handleUseAISuggestion}
                          >
                            Use This Estimate
                          </Button>
                        )}
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleDismissAISuggestion}
                        >
                          Dismiss
                        </Button>
                      </Box>
                    </>
                  ) : null}
                </Box>
              )}

              {/* Estimate Selection */}
              <CompactCardDeck
                deckType={deckType}
                selectedValue={selectedEstimate}
                onSelect={setSelectedEstimate}
              />

              <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleSetFinalEstimate}
                  disabled={!selectedEstimate}
                  startIcon={<CheckCircleIcon />}
                >
                  Set Estimate: {selectedEstimate || '?'}
                </Button>

                {jiraKey && jiraConfigured && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handlePushToJira}
                    disabled={!selectedEstimate || isPushing}
                    startIcon={isPushing ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                  >
                    Push to Jira
                  </Button>
                )}

                {onStartNewRound && (
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => onStartNewRound(item.id)}
                    startIcon={<RestartAltIcon />}
                  >
                    New Round
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {/* Comments Section */}
          <ItemComments
            itemId={item.id}
            comments={comments}
            jiraKey={jiraKey}
            jiraUrl={jiraUrl}
            isFacilitator={isFacilitator}
            participants={participants}
          />
        </CardContent>
      </Collapse>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default AsyncItemCard;
